/*
  Utility functions that should be part of JS and are typically provided by lodash
*/

type CallbackFunctionVariadic = (...args: any[]) => void;

/**
 * Computes the SHA-256 hash of a string and returns it as a hexadecimal string.
 *
 * @param {string} input - The string to be hashed.
 * @param {string} algorithm - The algorithm to use. Defaults to 'SHA-256'.
 * @returns {Promise<string>} A promise that resolves to the hash in hexadecimal format.
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest#converting_a_digest_to_a_hex_string|MDN Web Docs}
 */
export async function hashString(input: string, algorithm?: string): Promise<string> {
  const msgUint8 = new TextEncoder().encode(input);
  const algorithmToUse = algorithm || 'SHA-256';
  const hashBuffer = await window.crypto.subtle.digest(algorithmToUse, msgUint8);
  const hashArray = [...new Uint8Array(hashBuffer)];
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

// Copied over from: https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore#_debounce
export function debounce(
  func: CallbackFunctionVariadic,
  wait: number,
  immediate: boolean = false
): CallbackFunctionVariadic {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  return function (this: any, ...args: any[]) {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = undefined;
      if (!immediate) {
        func.apply(this, args);
      }
    }, wait);
    if (immediate && !timeout) {
      func.apply(this, args);
    }
  };
}

/**
 * Function that returns a promise that resolves or aborts after a specified timeout
 * https://dmitripavlutin.com/timeout-fetch-request/
 *
 * @param resource Path to resource that you want to fetch
 * @param timeout Timeout in milliseconds
 * @param options Fetch options
 * @returns Promise that resolves to the response
 */
export async function fetchWithTimeout(resource: string, options: any, timeout: number) {
  const controller = new AbortController();
  const id = setTimeout(() => {
    controller.abort();
    console.error('Request timeout exceeded');
  }, timeout); // Can refactor into AbortSignal.timeout once it's supported in more browsers
  const response = await fetch(resource, {
    ...options,
    signal: controller.signal,
  });

  clearTimeout(id);

  return response;
}

/**
 * Function that validates a URL
 *
 * @param url The URL to be validated
 * @param opts Dictionary of options opts.relative: boolean to validate if the URL is relative to current one
 * @returns True or false
 */
export function isValidUrl(url: string, opts?: { relative: boolean }): boolean {
  if (!url) {
    return false;
  }

  try {
    const urlObj = new URL(url, document.baseURI);
    if (opts?.relative) {
      return urlObj.origin === new URL(document.baseURI).origin;
    } else {
      return true;
    }
  } catch {
    return false;
  }
}

export const addEventListener = (
  el: HTMLElement,
  eventName: string,
  eventHandler: (e: Event) => void,
  selector?: string
) => {
  if (selector) {
    const wrappedHandler = (e: Event) => {
      if (e.target && (e.target as Element).closest(selector)) {
        eventHandler(e);
      }
    };
    el.addEventListener(eventName, wrappedHandler);
    return wrappedHandler;
  } else {
    el.addEventListener(eventName, eventHandler);
    return eventHandler;
  }
};
